import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../components/onboarding/OnboardingProvider';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import SearchableCourseSelect from '../components/SearchableCourseSelect';
import {
  UserPlus,
  PencilSimple,
  CurrencyDollar,
  CreditCard,
  ListBullets,
  Eye,
  SignOut,
  CheckCircle,
  XCircle,
  Spinner,
  CaretLeft,
  Compass,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const GENDERS = [
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

const YEARS_OF_STUDY = ['1', '2', '3', '4', '5'];

const initialSubForm = {
  sub_name: '', card_no: '', mobile: '', email: '', gender: 'M',
  entity_id: '', campus_id: '', category: 'STUDENT',
  student_id: '', student_course_id: '', curr_year_of_study: '1',
  subscription_plan: 'Fee',
  id_type: '1', id_number: '',
};

export default function CashierDashboardPage() {
  const { user, logout } = useAuth();
  const onb = useOnboarding();
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('home');
  const [logType, setLogType] = useState('registrations');
  const [logs, setLogs] = useState([]);
  const [expandedLog, setExpandedLog] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);

  // Cashier config
  const [cashierConfig, setCashierConfig] = useState(null); // { campus_id, campus_name, entity_id, sub_free_plan }

  // Form states
  const [subForm, setSubForm] = useState({ ...initialSubForm });
  const [submitting, setSubmitting] = useState(false);
  const [cardStatus, setCardStatus] = useState(null);
  const [categories, setCategories] = useState([]);
  const [idTypes, setIdTypes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [lastRegCode, setLastRegCode] = useState(null); // ambassador/WABA collection code

  // Country-code + phone rules + OTP verification for Add Subscriber
  const [countries, setCountries] = useState([]);              // [{code,mobile_prefix,...}]
  const [phoneRules, setPhoneRules] = useState(null);          // {defaults, east_africa_default, overrides}
  const [entityCountryCode, setEntityCountryCode] = useState(''); // e.g. "TZ"
  const [mobileLocal, setMobileLocal] = useState('');          // digits AFTER prefix
  const [otpValue, setOtpValue] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);         // seconds till resend allowed

  // Update subscriber (WABA / Ambassador finalize)
  const [searchCode, setSearchCode] = useState('');
  const [foundSub, setFoundSub] = useState(null); // pending record from /api/waba-subscribers
  const [finalizeForm, setFinalizeForm] = useState({ category: '', card_no: '', email: '', id_number: '', student_id: '', student_course_id: '', curr_year_of_study: '1' });
  const [finalizeCardStatus, setFinalizeCardStatus] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [finalizeSubmitting, setFinalizeSubmitting] = useState(false);

  // Recharge
  const [rechargeCard, setRechargeCard] = useState('');
  const [rechargeAmount, setRechargeAmount] = useState('');
  const [rechargeCategory, setRechargeCategory] = useState('Recharge'); // 'Recharge' | 'Special Offer'
  const [rechargeSubmitting, setRechargeSubmitting] = useState(false);
  const [rechargeCardStatus, setRechargeCardStatus] = useState(null); // null|'checking'|'invalid'|'unassigned'|'valid'
  const [rechargeCardHolder, setRechargeCardHolder] = useState('');

  const hdrs = () => {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch cashier's assigned config (campus, entity, sub_free_plan)
  const fetchCashierConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cashiers`, { withCredentials: true, headers: hdrs() });
      if (data.length > 0) {
        const c = data.find(rec => rec.user_id === user?.id) || data[0];
        setCashierConfig({
          campus_id: c.campus_id || '',
          campus_name: c.campus_name || '',
          entity_id: c.entity_id || '',
          entity_name: c.entity_name || '',
          sub_free_plan: c.sub_free_plan || 'No',
          offer: c.offer || 'No',
        });
      }
    } catch (e) { console.error(e); }
  }, [user?.id]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/cashier-dashboard/stats`, { withCredentials: true, headers: hdrs() });
      setStats(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscriber-categories`, { withCredentials: true, headers: hdrs() });
      setCategories(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchIdTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/subscriber-id-types`, { withCredentials: true, headers: hdrs() });
      setIdTypes(data);
    } catch (e) { console.error(e); }
  }, []);

  // Country + per-country phone-length rules. Both are needed to render the
  // fixed prefix chip and to validate the local-digits input.
  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/config/countries`, { withCredentials: true, headers: hdrs() });
      setCountries(Array.isArray(data) ? data : []);
    } catch (e) { setCountries([]); }
  }, []);

  const fetchPhoneRules = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/config/country-phone-rules`, { withCredentials: true, headers: hdrs() });
      setPhoneRules(data || null);
    } catch (e) { setPhoneRules(null); }
  }, []);

  const fetchCourses = useCallback(async (campusId) => {
    if (!campusId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/student-courses?campus_id=${campusId}`, { withCredentials: true, headers: hdrs() });
      setCourses(Array.isArray(data) ? data : []);
    } catch (e) { console.error('Failed to load courses', e); setCourses([]); }
  }, []);

  useEffect(() => { fetchStats(); fetchCategories(); fetchIdTypes(); fetchCashierConfig(); fetchCountries(); fetchPhoneRules(); }, [fetchStats, fetchCategories, fetchIdTypes, fetchCashierConfig, fetchCountries, fetchPhoneRules]);
  // Auto-refresh balances every 30s while the dashboard is the active tab.
  // Pauses on hidden tab to keep this gentle on the middleware.
  useEffect(() => {
    const tick = () => { if (!document.hidden) fetchStats(); };
    const id = setInterval(tick, 30000);
    const onVis = () => { if (!document.hidden) fetchStats(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [fetchStats]);
  useEffect(() => { if (cashierConfig?.campus_id) fetchCourses(cashierConfig.campus_id); }, [cashierConfig?.campus_id, fetchCourses]);

  // Resolve the cashier's entity country_code once the entity_id is known.
  useEffect(() => {
    if (!cashierConfig?.entity_id) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/entities`, { withCredentials: true, headers: hdrs() });
        if (!alive) return;
        const row = (Array.isArray(data) ? data : []).find(e => String(e.id) === String(cashierConfig.entity_id));
        setEntityCountryCode((row?.country_code || '').toUpperCase());
      } catch (e) { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [cashierConfig?.entity_id]);

  // Derived: prefix + digit rules for the resolved country.
  const currentCountry = countries.find(c => (c.code || '').toUpperCase() === entityCountryCode) || null;
  const mobilePrefix   = currentCountry?.mobile_prefix || '';   // e.g. "255"
  const digitRules = (() => {
    const east = phoneRules?.east_africa_default || { min_digits: 9, max_digits: 9 };
    const def  = phoneRules?.defaults || { min_digits: 7, max_digits: 15 };
    const ov   = phoneRules?.overrides || {};
    if (ov[entityCountryCode]) return ov[entityCountryCode];
    const eaSet = new Set(['TZ', 'KE', 'UG', 'RW', 'BI', 'ET', 'SS']);
    if (eaSet.has(entityCountryCode)) return east;
    return def;
  })();

  // OTP resend cooldown ticker.
  useEffect(() => {
    if (otpCountdown <= 0) return;
    const t = setTimeout(() => setOtpCountdown(v => Math.max(0, v - 1)), 1000);
    return () => clearTimeout(t);
  }, [otpCountdown]);

  // The full phone number we track in subForm.mobile is prefix + local digits.
  // Keeping subForm.mobile in sync as the user types.
  useEffect(() => {
    const full = mobilePrefix + (mobileLocal || '');
    setSubForm(prev => (prev.mobile === full ? prev : { ...prev, mobile: full }));
    // If the local number changes, invalidate any prior OTP verification.
    setOtpVerified(false);
    setOtpSent(false);
    setOtpValue('');
  }, [mobileLocal, mobilePrefix]);

  const localDigitsValid = mobileLocal.length >= (digitRules.min_digits || 0)
                        && mobileLocal.length <= (digitRules.max_digits || 20);

  const handleSendOtp = async () => {
    if (!mobilePrefix) { toast.error('Cannot send OTP — country prefix not configured for this entity.'); return; }
    if (!localDigitsValid) { toast.error(`Phone must be ${digitRules.min_digits}${digitRules.max_digits !== digitRules.min_digits ? `–${digitRules.max_digits}` : ''} digits.`); return; }
    setOtpSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/cashiers/subscribers/otp/send`,
        { mobile: mobilePrefix + mobileLocal },
        { withCredentials: true, headers: hdrs() },
      );
      setOtpSent(true);
      setOtpVerified(false);
      setOtpValue('');
      setOtpCountdown(Number(data?.cooldown) || 60);
      // Surface real delivery state to the cashier so they never wait for an
      // SMS that was silently dropped by the gateway (kill-switch or 3rd-party
      // gateway rejection).
      if (data?.channel === 'sms') {
        toast.success('OTP sent via SMS');
      } else if (data?.channel === 'dev-console') {
        toast.success('OTP generated (dev mode) — check backend logs / response');
      } else {
        const hint = data?.delivery_error || 'SMS gateway did not deliver the code';
        toast.error(`OTP not delivered: ${hint}. Please retry in a moment or contact support.`);
        setOtpSent(false); // Do NOT lock the field on a failed send — cashier can immediately re-send.
      }
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to send OTP'));
    } finally { setOtpSending(false); }
  };

  const handleVerifyOtp = async (codeOverride) => {
    // Auto-verify passes the freshly-typed code so we don't depend on setState timing.
    const code = (codeOverride ?? otpValue).trim();
    if (!code) { toast.error('Enter the OTP code'); return; }
    setOtpVerifying(true);
    try {
      await axios.post(
        `${API_URL}/api/cashiers/subscribers/otp/verify`,
        { mobile: mobilePrefix + mobileLocal, otp: code },
        { withCredentials: true, headers: hdrs() },
      );
      setOtpVerified(true);
      toast.success('Phone verified');
    } catch (e) {
      setOtpVerified(false);
      // Reset the OTP field on a wrong code so the user can retry cleanly.
      setOtpValue('');
      toast.error(formatApiError(e, 'Incorrect OTP'));
    } finally { setOtpVerifying(false); }
  };


  const updateSub = (field, value) => {
    setSubForm(prev => {
      // When the cashier switches category away from STUDENT, drop the
      // student-only fields so the upstream middleware (which validates them
      // against /get_student_courses for the campus) doesn't reject the
      // registration with "Campus ID does not match" / "course you picked
      // doesn't belong to this campus".
      if (field === 'category') {
        const isStudent = (value || '').toUpperCase() === 'STUDENT';
        return {
          ...prev,
          [field]: value,
          student_id: isStudent ? prev.student_id : '',
          student_course_id: isStudent ? prev.student_course_id : '',
          curr_year_of_study: isStudent ? (prev.curr_year_of_study || '1') : '',
        };
      }
      return { ...prev, [field]: value };
    });
    if (fieldErrors[field]) setFieldErrors(prev => ({ ...prev, [field]: false }));
  };

  // Card verification
  const verifyCard = async (cardNo) => {
    if (!cardNo || cardNo.length < 5) { setCardStatus(null); return; }
    setCardStatus('checking');
    try {
      const entityId = cashierConfig?.entity_id || '';
      const params = entityId ? `?entity_id=${entityId}` : '';
      const { data } = await axios.get(`${API_URL}/api/cards/verify/${cardNo}${params}`, { withCredentials: true, headers: hdrs() });
      if (!data.exists) { setCardStatus('invalid'); }
      else if (data.occupied) { setCardStatus('occupied'); }
      else { setCardStatus('valid'); }
    } catch (e) { setCardStatus('invalid'); }
  };

  const isAmbassador = user?.role === 'ambassador';

  // Validate form and return errors
  const validateSubForm = () => {
    const errors = {};
    if (!subForm.sub_name.trim()) errors.sub_name = true;
    if (!isAmbassador && !subForm.card_no.trim()) errors.card_no = true;
    if (!mobileLocal.trim() || !localDigitsValid) errors.mobile = true;
    if (!subForm.gender) errors.gender = true;
    if (!subForm.category) errors.category = true;
    return errors;
  };

  // Add subscriber
  const handleAddSubscriber = async () => {
    const errors = validateSubForm();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error('Please fill all required fields highlighted in red');
      return;
    }
    if (!isAmbassador && cardStatus !== 'valid') {
      toast.error('Card number is invalid or occupied');
      setFieldErrors({ card_no: true });
      return;
    }
    if (!otpVerified) {
      toast.error('Please verify the phone number via OTP first');
      setFieldErrors(prev => ({ ...prev, mobile: true }));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...subForm,
        campus_id: cashierConfig?.campus_id || '',
        is_ambassador_reg: isAmbassador ? 'Y' : 'N',
        is_waba_reg: 'N',
        wa_reg_no: null,
        waba_reg_code: null,
      };
      if (isAmbassador) {
        // Ambassador registrations have no card; clear it from payload
        delete payload.card_no;
      }
      const { data } = await axios.post(`${API_URL}/api/subscribers`, payload, { withCredentials: true, headers: hdrs() });
      if (isAmbassador && data?.reg_code) {
        setLastRegCode({ code: data.reg_code, name: data.sub_name, mobile: data.mobile });
        toast.success('Ambassador registration created — share the collection code with the subscriber');
      } else {
        toast.success('Subscriber registered successfully');
      }
      resetSubForm();
      fetchStats();
      if (!(isAmbassador && data?.reg_code)) setView('home');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to register subscriber'));
    } finally { setSubmitting(false); }
  };

  const resetSubForm = () => {
    setSubForm({ ...initialSubForm });
    setCardStatus(null);
    setFieldErrors({});
    setMobileLocal('');
    setOtpValue('');
    setOtpSent(false);
    setOtpVerified(false);
    setOtpCountdown(0);
  };

  // Lookup pending WABA/ambassador registration by reg code
  const searchSubscriber = async () => {
    if (!searchCode) { toast.error('Enter a registration code'); return; }
    setSearchLoading(true);
    setFoundSub(null);
    setFinalizeCardStatus(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/waba-subscribers`,
        { waba_reg_code: searchCode },
        { withCredentials: true, headers: hdrs() }
      );
      setFoundSub(data);
      setFinalizeForm({
        category: data.category || '',
        card_no: data.card_no || '',
        email: data.email || '',
        id_number: data.id_number || '',
        student_id: data.student_id || '',
        student_course_id: data.course || '',
        curr_year_of_study: data.curr_course_year || '1',
      });
      // Pre-load courses for the campus from this lookup
      if (data.campus_id) fetchCourses(data.campus_id);
    } catch (e) {
      toast.error(formatApiError(e, 'No pending registration found for this code'));
    }
    finally { setSearchLoading(false); }
  };

  const updateFinalizeField = (field, value) => {
    setFinalizeForm(prev => ({ ...prev, [field]: value }));
  };

  // Verify the card the cashier is assigning at finalize time
  const verifyFinalizeCard = async (cardNo) => {
    if (!cardNo || cardNo.length < 5) { setFinalizeCardStatus(null); return; }
    setFinalizeCardStatus('checking');
    try {
      const entityId = cashierConfig?.entity_id || '';
      const params = entityId ? `?entity_id=${entityId}` : '';
      const { data } = await axios.get(`${API_URL}/api/cards/verify/${cardNo}${params}`, { withCredentials: true, headers: hdrs() });
      if (!data.exists) setFinalizeCardStatus('invalid');
      else if (data.occupied) setFinalizeCardStatus('occupied');
      else setFinalizeCardStatus('valid');
    } catch (e) { setFinalizeCardStatus('invalid'); }
  };

  // Verify the card the cashier is recharging — must exist in entity AND be occupied
  const verifyRechargeCard = async (cardNo) => {
    if (!cardNo || cardNo.length < 5) { setRechargeCardStatus(null); setRechargeCardHolder(''); return; }
    setRechargeCardStatus('checking');
    setRechargeCardHolder('');
    try {
      const entityId = cashierConfig?.entity_id || '';
      const params = entityId ? `?entity_id=${entityId}` : '';
      const { data } = await axios.get(`${API_URL}/api/cards/verify/${cardNo}${params}`, { withCredentials: true, headers: hdrs() });
      if (!data.exists) setRechargeCardStatus('invalid');
      else if (!data.occupied) setRechargeCardStatus('unassigned');
      else {
        setRechargeCardStatus('valid');
        setRechargeCardHolder(data.sub_name || '');
      }
    } catch (e) { setRechargeCardStatus('invalid'); }
  };

  // Submit the finalize call → reuses /api/subscribers (create_subscriber) with is_waba_reg=Y
  const handleFinalizeSubscriber = async () => {
    if (!foundSub) return;
    if (!finalizeForm.category) { toast.error('Category is required'); return; }
    if (!finalizeForm.card_no.trim()) { toast.error('Card number is required'); return; }
    if (finalizeCardStatus !== 'valid') { toast.error('Card number is invalid or already assigned'); return; }
    const isStudentCat = (finalizeForm.category || '').toUpperCase() === 'STUDENT';
    if (!isStudentCat && !finalizeForm.id_number.trim()) {
      toast.error('ID number is required');
      return;
    }
    setFinalizeSubmitting(true);
    try {
      const payload = {
        sub_name: foundSub.sub_name,
        card_no: finalizeForm.card_no,
        mobile: foundSub.mobile,                                   // already collected on the pending record
        email: finalizeForm.email || foundSub.email || '',
        gender: foundSub.gender || 'N/A',
        campus_id: foundSub.campus_id || cashierConfig?.campus_id || '',
        category: finalizeForm.category,
        student_id: isStudentCat ? (finalizeForm.student_id || foundSub.student_id || '') : '',
        student_course_id: finalizeForm.student_course_id || '',
        curr_year_of_study: finalizeForm.curr_year_of_study || '',
        subscription_plan: foundSub.subscription_plan || 'Fee',
        is_ambassador_reg: 'N',
        id_type: foundSub.id_type || '1',
        id_number: finalizeForm.id_number || foundSub.id_number || '',
        is_waba_reg: 'Y',
        wa_reg_no: foundSub.wa_reg_no || null,
        waba_reg_code: foundSub.waba_reg_code || searchCode,
      };
      await axios.post(`${API_URL}/api/subscribers`, payload, { withCredentials: true, headers: hdrs() });
      toast.success('Registration finalized — card assigned');
      setSearchCode('');
      setFoundSub(null);
      setFinalizeCardStatus(null);
      fetchStats();
      setView('home');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to finalize registration'));
    } finally { setFinalizeSubmitting(false); }
  };

  // Fetch logs
  const fetchLogs = async (type) => {
    setLogType(type);
    setLogsLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/cashier-dashboard/today-logs?log_type=${type}`, { withCredentials: true, headers: hdrs() });
      setLogs(data);
    } catch (e) { toast.error('Failed to fetch logs'); }
    finally { setLogsLoading(false); }
  };

  const isStudent = subForm.category === 'STUDENT';
  const showFreePlan = cashierConfig?.sub_free_plan === 'Yes';

  // ===== RENDER =====
  return (
    <div className="min-h-screen bg-[#F4F5F4]" data-testid="cashier-dashboard">
      {/* Top Bar */}
      <div className="bg-[#0A2540] text-white px-4 py-3.5 flex items-center justify-between sticky top-0 z-50">
        {view !== 'home' ? (
          <button onClick={() => { setView('home'); setFieldErrors({}); }} className="flex items-center gap-1.5 text-base font-medium" data-testid="back-home-btn">
            <CaretLeft size={22} weight="bold" /> Back
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_ecowater-cloud/artifacts/tzhqe5c4_ECOWATER_WORDMARK_LOGO.png"
              alt="Ecowater"
              className="h-5 w-auto object-contain brightness-0 invert"
            />
          </div>
        )}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium opacity-90" style={{ fontFamily: 'Ubuntu, sans-serif' }}>{user?.fullname}</span>
          {onb?.canShowSetupGuide && (
            <button
              type="button"
              onClick={() => onb.launch()}
              data-testid="cashier-setup-guide-btn"
              aria-label="Setup guide"
              className="opacity-80 hover:opacity-100 transition"
            >
              <Compass size={20} weight="bold" />
            </button>
          )}
          <button onClick={logout} data-testid="cashier-logout-btn"><SignOut size={22} /></button>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pb-8">
        {/* ========== HOME ========== */}
        {view === 'home' && (
          <>
            {/* ========== TODAY ========== */}
            <div className="mt-4 mb-4 bg-white border border-[#E2E8E5] rounded-2xl p-4">
              <p className="text-[14px] font-bold text-[#1A1D1C] mb-3" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Today</p>
              <div className="grid grid-cols-2 gap-3">
                <StatCard label="Sub Fees" value={Number(stats?.subscription_fees || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unit={stats?.currency || ''} testId="stat-sub-fees" />
                <StatCard label="Recharges" value={Number(stats?.recharges_collected || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unit={stats?.currency || ''} testId="stat-recharges" />
                <StatCard label="Card Bal." value={Number(stats?.card_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} unit="" testId="stat-card-bal" muted />
                <StatCard label="Float Bal." value={Number(stats?.float_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} unit={stats?.currency || ''} testId="stat-float-bal" muted />
              </div>
            </div>

            {/* ========== ACTIONS — SUBSCRIBERS ========== */}
            <div className="mb-4 bg-white border border-[#E2E8E5] rounded-2xl p-4">
              <p className="text-[14px] font-bold text-[#1A1D1C] mb-3" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Subscribers</p>
              <div className="grid grid-cols-3 gap-3">
                <IconTile icon={UserPlus} label="Add"
                  onClick={() => { resetSubForm(); setLastRegCode(null); setView('addSub'); }} testId="btn-add-sub" />
                <IconTile icon={PencilSimple} label="Update"
                  onClick={() => { setSearchCode(''); setFoundSub(null); setFinalizeCardStatus(null); setView('updateSub'); }} testId="btn-update-sub" />
                {(stats?.recharge_enabled || 'Y') === 'Y' && (
                  <IconTile icon={CurrencyDollar} label="Recharge"
                    onClick={() => { setRechargeCard(''); setRechargeAmount(''); setRechargeCardStatus(null); setRechargeCardHolder(''); setView('recharge'); }} testId="btn-recharge" />
                )}
              </div>
            </div>

            {/* ========== TODAY'S LOGS ========== */}
            <div className="mb-2 bg-white border border-[#E2E8E5] rounded-2xl p-4">
              <p className="text-[14px] font-bold text-[#1A1D1C] mb-3" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Today&apos;s Logs</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => { fetchLogs('registrations'); setView('logs'); }}
                  className="flex flex-col items-center justify-center gap-2 bg-[#F8F9FA] border border-[#E2E8E5] rounded-xl py-4 active:bg-[#EEF2F4] transition-all"
                  data-testid="btn-log-reg">
                  <div className="w-12 h-12 rounded-full bg-[#0779BC]/10 flex items-center justify-center">
                    <ListBullets size={20} weight="regular" className="text-[#0779BC]" />
                  </div>
                  <p className="text-[13px] text-[#1A1D1C] font-medium leading-tight" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Registrations</p>
                  <p className="text-[11px] text-[#6C7A75] leading-tight" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{stats?.today_registrations || 0} today</p>
                </button>
                <button onClick={() => { fetchLogs('recharges'); setView('logs'); }}
                  className="flex flex-col items-center justify-center gap-2 bg-[#F8F9FA] border border-[#E2E8E5] rounded-xl py-4 active:bg-[#EEF2F4] transition-all"
                  data-testid="btn-log-rech">
                  <div className="w-12 h-12 rounded-full bg-[#0779BC]/10 flex items-center justify-center">
                    <ListBullets size={20} weight="regular" className="text-[#0779BC]" />
                  </div>
                  <p className="text-[13px] text-[#1A1D1C] font-medium leading-tight" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Recharges</p>
                  <p className="text-[11px] text-[#6C7A75] leading-tight" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{stats?.today_recharges || 0} today</p>
                </button>
              </div>
            </div>
          </>
        )}

        {/* ========== ADD SUBSCRIBER ========== */}
        {view === 'addSub' && (
          <div className="mt-4 space-y-4" data-testid="cashier-add-sub">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-[#0779BC]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                {isAmbassador ? 'Register via Ambassador' : 'Add Subscriber'}
              </h2>
              {cashierConfig?.campus_name && (
                <span className="text-xs bg-[#0779BC]/10 text-[#0779BC] px-2.5 py-1 rounded-full font-medium">{cashierConfig.campus_name}</span>
              )}
            </div>
            {isAmbassador && (
              <div className="rounded-xl border border-[#2E7D5B]/30 bg-[#2E7D5B]/5 px-3 py-2.5 text-xs text-[#2E7D5B]" data-testid="ambassador-banner" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                You are registering as an <span className="font-semibold">Ambassador</span>. Subscribers will receive a <span className="font-semibold">collection code</span> to redeem their card with a cashier.
              </div>
            )}

            {lastRegCode && (
              <div className="rounded-2xl border-2 border-[#2E7D5B] bg-[#2E7D5B]/5 p-4 space-y-2" data-testid="reg-code-panel">
                <p className="text-[10px] uppercase tracking-widest text-[#2E7D5B] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  Collection Code
                </p>
                <p className="text-3xl font-bold text-[#1A1D1C] tracking-wider" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="reg-code-value">
                  {lastRegCode.code}
                </p>
                <p className="text-xs text-[#6C7A75]">For <span className="font-medium">{lastRegCode.name}</span> · {lastRegCode.mobile}</p>
                <div className="flex gap-2 pt-2">
                  <Button onClick={() => { navigator.clipboard?.writeText(lastRegCode.code); toast.success('Code copied'); }}
                    variant="outline" className="flex-1 h-10 rounded-xl border-[#2E7D5B] text-[#2E7D5B]" data-testid="reg-code-copy">
                    Copy code
                  </Button>
                  <Button onClick={() => { setLastRegCode(null); setView('home'); }}
                    className="flex-1 h-10 rounded-xl bg-[#2E7D5B] hover:bg-[#2E7D5B]/90 text-white" data-testid="reg-code-done">
                    Done
                  </Button>
                </div>
              </div>
            )}

            {/* Bordered form card */}
            <div className="bg-white border border-[#E2E8E5] rounded-2xl shadow-sm overflow-hidden" data-testid="add-sub-form-card">
              {/* Card header */}
              <div className="px-5 py-3 bg-gradient-to-r from-[#0779BC]/5 via-white to-[#0779BC]/5 border-b border-[#E2E8E5]">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-[#0779BC] flex items-center justify-center">
                    <UserPlus size={14} weight="bold" className="text-white" />
                  </div>
                  <p className="text-xs uppercase tracking-widest text-[#0779BC] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    Subscriber Details
                  </p>
                </div>
                <p className="text-[10px] uppercase tracking-wider text-[#6C7A75] font-medium mt-1.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  Fields marked * are required
                </p>
              </div>

              {/* Card body — all form fields */}
              <div className="p-5 space-y-4">
                {/* Mobile — first field, own row, with country-code chip */}
                <Field label="Mobile *" error={fieldErrors.mobile}>
                  <div className="flex items-stretch gap-0 rounded-xl border overflow-hidden h-11" style={{ borderColor: fieldErrors.mobile ? '#B94040' : '#E2E8E5', borderWidth: fieldErrors.mobile ? 2 : 1 }}>
                    <div
                      className="flex items-center justify-center px-3 bg-[#F1F5F4] text-[#0779BC] text-sm font-semibold border-r border-[#E2E8E5] shrink-0"
                      style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                      data-testid="csub-mobile-prefix"
                      title={entityCountryCode ? `${entityCountryCode} · +${mobilePrefix}` : 'Country prefix not configured'}
                    >
                      {mobilePrefix ? `+${mobilePrefix}` : '—'}
                    </div>
                    <Input
                      value={mobileLocal}
                      onChange={e => {
                        let v = e.target.value.replace(/\D/g, '').slice(0, digitRules.max_digits || 15);
                        // Common East-Africa habit: cashiers type the local number as "0712345678" —
                        // but the "+CC" prefix already covers the leading zero. Strip it as soon as a
                        // second digit is entered so the format stays canonical.
                        if (v.length >= 2) v = v.replace(/^0+/, '');
                        setMobileLocal(v);
                      }}
                      placeholder=""
                      inputMode="tel"
                      className="rounded-none border-0 h-full focus-visible:ring-0 focus-visible:ring-offset-0"
                      data-testid="csub-mobile"
                      maxLength={digitRules.max_digits || 15}
                    />
                  </div>
                  <p className="text-[10px] text-[#6C7A75] mt-1" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    {digitRules.min_digits === digitRules.max_digits
                      ? `Enter ${digitRules.min_digits} digits`
                      : `Enter ${digitRules.min_digits}–${digitRules.max_digits} digits`}
                    {mobileLocal && !localDigitsValid && <span className="text-[#B94040]"> · invalid length</span>}
                    {otpVerified && <span className="text-[#2E7D5B]"> · verified ✓</span>}
                  </p>
                </Field>

                {/* OTP verification (skip for ambassador — they have no card / phone verify need) */}
                {!isAmbassador && (
                  <div className="rounded-xl border border-[#E2E8E5] bg-[#F8FAFB] p-3 space-y-2" data-testid="csub-otp-block">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] uppercase tracking-widest text-[#6C7A75] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        Phone verification
                      </p>
                      {otpVerified && (
                        <button
                          type="button"
                          onClick={() => {
                            setOtpVerified(false);
                            setOtpSent(false);
                            setOtpValue('');
                          }}
                          className="text-[10px] text-[#0779BC] hover:underline font-medium"
                          data-testid="csub-otp-change"
                        >
                          Change number
                        </button>
                      )}
                    </div>

                    {otpVerified ? (
                      <div className="h-11 flex items-center px-3 rounded-xl bg-[#EAF6EF] text-[#2E7D5B] text-sm font-medium" data-testid="csub-otp-verified">
                        <CheckCircle size={16} weight="fill" className="mr-2" /> Phone Verified.
                      </div>
                    ) : (
                      <div className="flex items-stretch gap-2">
                        {/* LEFT: Send OTP / Resend button */}
                        <Button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={otpSending || !localDigitsValid || !mobilePrefix || (otpSent && otpCountdown > 0)}
                          className="h-11 bg-[#0779BC] hover:bg-[#0779BC]/90 text-white rounded-xl px-4 disabled:opacity-50 shrink-0"
                          data-testid={otpSent ? 'csub-otp-resend' : 'csub-otp-send'}
                        >
                          {otpSending
                            ? 'Sending…'
                            : otpSent
                              ? (otpCountdown > 0 ? `Resend in ${otpCountdown}s` : 'Resend OTP')
                              : 'Send OTP'}
                        </Button>

                        {/* RIGHT: OTP input — auto-verifies once 6 digits are entered */}
                        <Input
                          value={otpValue}
                          onChange={e => {
                            const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtpValue(v);
                            // Auto-verify the instant the user completes the 6-digit code.
                            if (v.length === 6 && otpSent && !otpVerifying && !otpVerified) {
                              handleVerifyOtp(v);
                            }
                          }}
                          placeholder={otpSent ? 'Enter 6-digit code' : 'Send OTP first'}
                          inputMode="numeric"
                          disabled={!otpSent || otpVerifying}
                          className="rounded-xl h-11 flex-1 text-right"
                          data-testid="csub-otp-input"
                          maxLength={6}
                        />
                        {otpVerifying && (
                          <div className="h-11 flex items-center px-3 rounded-xl bg-[#F1F5F4] text-[#0779BC] text-sm font-medium shrink-0" data-testid="csub-otp-verifying">
                            Verifying…
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <Field label="Full Name *" error={fieldErrors.sub_name}>
                  <Input value={subForm.sub_name} onChange={e => updateSub('sub_name', e.target.value)}
                    placeholder="Full name" className={`rounded-xl h-11 ${fieldErrors.sub_name ? 'border-[#B94040] border-2' : 'border-[#E2E8E5]'}`} data-testid="csub-name" />
                </Field>

                <div className="grid grid-cols-2 gap-3">
                  <Field label="Gender *" error={fieldErrors.gender}>
                    <Select value={subForm.gender} onValueChange={v => updateSub('gender', v)}>
                      <SelectTrigger className={`rounded-xl h-11 ${fieldErrors.gender ? 'border-[#B94040] border-2' : 'border-[#E2E8E5]'}`} data-testid="csub-gender"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {GENDERS.map(g => <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Category *" error={fieldErrors.category}>
                    <Select value={subForm.category} onValueChange={v => updateSub('category', v)}>
                      <SelectTrigger className={`rounded-xl h-11 ${fieldErrors.category ? 'border-[#B94040] border-2' : 'border-[#E2E8E5]'}`} data-testid="csub-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}
                        {categories.length === 0 && <SelectItem value="STUDENT">STUDENT</SelectItem>}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <Field label={`Card Number ${isAmbassador ? '' : '*'}`} error={fieldErrors.card_no}>
                  {isAmbassador ? (
                    <div className="rounded-xl border border-dashed border-[#2E7D5B]/40 bg-[#2E7D5B]/5 px-3 py-2.5 text-xs text-[#2E7D5B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      Ambassador flow — subscriber will receive a collection code; card is assigned later by a cashier.
                    </div>
                  ) : (
                    <div className="relative">
                      <Input value={subForm.card_no} onChange={e => { const v = e.target.value.replace(/\D/g, ''); updateSub('card_no', v); if (v.length >= 10) verifyCard(v); else setCardStatus(null); }}
                        placeholder="Enter card number" inputMode="numeric" className={`rounded-xl h-11 pr-10 ${fieldErrors.card_no ? 'border-[#B94040] border-2' : 'border-[#E2E8E5]'}`} data-testid="csub-card" />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {cardStatus === 'checking' && <Spinner size={18} className="animate-spin text-[#6C7A75]" />}
                        {cardStatus === 'valid' && <CheckCircle size={18} weight="fill" className="text-[#2E7D5B]" />}
                        {(cardStatus === 'invalid' || cardStatus === 'occupied') && <XCircle size={18} weight="fill" className="text-[#B94040]" />}
                      </div>
                    </div>
                  )}
                  {!isAmbassador && cardStatus === 'invalid' && <p className="text-xs text-[#B94040] mt-1">Card not found in your entity</p>}
                  {!isAmbassador && cardStatus === 'occupied' && <p className="text-xs text-[#B94040] mt-1">Card is already assigned</p>}
                  {!isAmbassador && cardStatus === 'valid' && <p className="text-xs text-[#2E7D5B] mt-1">Card available</p>}
                </Field>

                <Field label="Email">
                  <Input type="email" value={subForm.email} onChange={e => updateSub('email', e.target.value)}
                    placeholder="email@... (optional)" className={`rounded-xl h-11 ${fieldErrors.email ? 'border-[#B94040] border-2' : 'border-[#E2E8E5]'}`} data-testid="csub-email" />
                </Field>

                {/* Student fields */}
                {isStudent && (
                  <div className="pt-2 mt-2 border-t border-dashed border-[#E2E8E5] space-y-4">
                    <p className="text-[10px] uppercase tracking-widest text-[#6C7A75] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      Student information
                    </p>
                    <Field label="Student ID">
                      <Input value={subForm.student_id} onChange={e => updateSub('student_id', e.target.value)}
                        placeholder="Student ID (optional)" className="rounded-xl border-[#E2E8E5] h-11" data-testid="csub-student-id" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Course Name">
                        <SearchableCourseSelect
                          value={subForm.student_course_id}
                          onChange={(v) => updateSub('student_course_id', v)}
                          courses={courses}
                          placeholder={courses.length === 0 ? 'No courses configured for this campus' : 'Select course'}
                          testid="csub-course"
                        />
                      </Field>
                      <Field label="Year of Study">
                        <Select value={subForm.curr_year_of_study} onValueChange={v => updateSub('curr_year_of_study', v)}>
                          <SelectTrigger className="rounded-xl border-[#E2E8E5] h-11" data-testid="csub-year"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {YEARS_OF_STUDY.map(y => <SelectItem key={y} value={y}>Year {y}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  </div>
                )}

                {/* Non-student ID section */}
                {!isStudent && (
                  <div className="pt-2 mt-2 border-t border-dashed border-[#E2E8E5] space-y-4">
                    <p className="text-[10px] uppercase tracking-widest text-[#6C7A75] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      Identification
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="ID Type">
                        <Select value={subForm.id_type} onValueChange={v => updateSub('id_type', v)}>
                          <SelectTrigger className="rounded-xl border-[#E2E8E5] h-11" data-testid="csub-id-type"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {idTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.id_name}</SelectItem>)}
                            {idTypes.length === 0 && <SelectItem value="1">National ID</SelectItem>}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="ID Number">
                        <Input value={subForm.id_number} onChange={e => updateSub('id_number', e.target.value)}
                          placeholder="ID number" className="rounded-xl border-[#E2E8E5] h-11" data-testid="csub-id-number" />
                      </Field>
                    </div>
                  </div>
                )}

                <Field label="Subscription Plan">
                  <Select value={subForm.subscription_plan} onValueChange={v => updateSub('subscription_plan', v)}>
                    <SelectTrigger className="rounded-xl border-[#E2E8E5] h-11" data-testid="csub-plan"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fee">Fee</SelectItem>
                      {showFreePlan && <SelectItem value="Free">Free</SelectItem>}
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {/* Card footer with submit button */}
              <div className="px-5 py-4 bg-[#F8FAFB] border-t border-[#E2E8E5]">
                <Button onClick={handleAddSubscriber} disabled={submitting || (!isAmbassador && !otpVerified)}
                  className="w-full h-12 bg-[#0779BC] hover:bg-[#0779BC]/90 text-white rounded-xl text-base font-medium disabled:opacity-60" data-testid="csub-submit">
                  {submitting ? 'Registering...' : (!isAmbassador && !otpVerified ? 'Verify phone via OTP to continue' : 'Register Subscriber')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ========== UPDATE SUBSCRIBER (Finalize Pending Reg) ========== */}
        {view === 'updateSub' && (
          <div className="mt-4 space-y-4" data-testid="cashier-update-sub">
            <h2 className="text-lg font-semibold text-[#0779BC]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>Finalize Registration</h2>
            <p className="text-xs text-[#6C7A75]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
              Enter the registration code the subscriber received via WABA or from their ambassador. Review their details, assign a card, and complete the registration.
            </p>

            <Field label="Registration Code *">
              <div className="flex gap-2">
                <Input value={searchCode} onChange={e => setSearchCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="e.g. 53124" inputMode="numeric" className="rounded-xl border-[#E2E8E5] h-11 flex-1" data-testid="usub-code-search" />
                <Button onClick={searchSubscriber} disabled={searchLoading}
                  className="bg-[#0779BC] hover:bg-[#0779BC]/90 text-white rounded-xl h-11 px-4" data-testid="usub-search-btn">
                  {searchLoading ? <Spinner size={18} className="animate-spin" /> : 'Get Details'}
                </Button>
              </div>
            </Field>

            {foundSub && (
              <>
                <div className="bg-white rounded-xl border border-[#E2E8E5] p-4 space-y-3" data-testid="found-sub-card">
                  <div className="flex items-center gap-3 pb-3 border-b border-[#E2E8E5]">
                    <div className="w-10 h-10 bg-[#0779BC] rounded-full flex items-center justify-center text-white font-bold">
                      {(foundSub.sub_name || 'U').charAt(0)}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-[#1A1D1C]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>{foundSub.sub_name}</p>
                      <p className="text-xs text-[#6C7A75]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                        {foundSub.category} · CODE {foundSub.waba_reg_code}
                      </p>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#D97706]/10 text-[#D97706] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {foundSub.record_stat || 'PENDING'}
                    </span>
                  </div>
                  <InfoRow label="Mobile" value={foundSub.mobile} />
                  <InfoRow label="Campus" value={foundSub.campus_name || foundSub.campus_code} />
                  {foundSub.campus_name && foundSub.origin_campus_id && foundSub.campus_id !== foundSub.origin_campus_id && (
                    <p className="text-[10px] text-[#6C7A75] italic" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      Campus overridden to your assigned campus ({foundSub.campus_name})
                    </p>
                  )}
                  <InfoRow label="Plan" value={`${foundSub.subscription_plan}${foundSub.sub_fee_amount ? ` · ${Number(foundSub.sub_fee_amount).toLocaleString()}` : ''}`} />
                  <InfoRow label="Gender" value={foundSub.gender === 'M' ? 'Male' : foundSub.gender === 'F' ? 'Female' : foundSub.gender} />
                  <InfoRow label="Registered by" value={foundSub.registered_by} />
                </div>

                {/* Editable missing fields */}
                <div className="bg-[#0779BC]/5 rounded-xl border border-[#0779BC]/30 p-4 space-y-3">
                  <p className="text-[10px] uppercase tracking-wider text-[#0779BC] font-semibold" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    Complete Missing Details
                  </p>

                  <Field label="Category *">
                    <Select value={finalizeForm.category} onValueChange={v => updateFinalizeField('category', v)}>
                      <SelectTrigger className="rounded-xl border-[#E2E8E5] h-11" data-testid="fin-category">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.category} value={c.category}>{c.category}</SelectItem>)}
                        {categories.length === 0 && <SelectItem value="STUDENT">STUDENT</SelectItem>}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field label="Card Number *">
                    <div className="relative">
                      <Input value={finalizeForm.card_no}
                        onChange={e => { const v = e.target.value.replace(/\D/g, ''); updateFinalizeField('card_no', v); if (v.length >= 10) verifyFinalizeCard(v); else setFinalizeCardStatus(null); }}
                        placeholder="Enter card number" inputMode="numeric"
                        className="rounded-xl h-11 pr-10 border-[#E2E8E5]" data-testid="fin-card" />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {finalizeCardStatus === 'checking' && <Spinner size={18} className="animate-spin text-[#6C7A75]" />}
                        {finalizeCardStatus === 'valid' && <CheckCircle size={18} weight="fill" className="text-[#0779BC]" />}
                        {(finalizeCardStatus === 'invalid' || finalizeCardStatus === 'occupied') && <XCircle size={18} weight="fill" className="text-[#B94040]" />}
                      </div>
                    </div>
                    {finalizeCardStatus === 'invalid' && <p className="text-xs text-[#B94040] mt-1">Card not found in your entity</p>}
                    {finalizeCardStatus === 'occupied' && <p className="text-xs text-[#B94040] mt-1">Card is already assigned</p>}
                    {finalizeCardStatus === 'valid' && <p className="text-xs text-[#0779BC] mt-1">Card available</p>}
                  </Field>

                  <Field label={foundSub.email ? 'Email' : 'Email *'}>
                    <Input type="email" value={finalizeForm.email} onChange={e => updateFinalizeField('email', e.target.value)}
                      placeholder={foundSub.email ? 'email@...' : 'email@... (required — not on file)'} className="rounded-xl border-[#E2E8E5] h-11" data-testid="fin-email" />
                  </Field>

                  {(finalizeForm.category || '').toUpperCase() === 'STUDENT' ? (
                    <>
                      <Field label="Student ID (optional)">
                        <Input value={finalizeForm.student_id} onChange={e => updateFinalizeField('student_id', e.target.value)}
                          placeholder="Student ID (optional)" className="rounded-xl border-[#E2E8E5] h-11" data-testid="fin-student-id" />
                      </Field>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="Course">
                          <SearchableCourseSelect
                            value={finalizeForm.student_course_id}
                            onChange={(v) => updateFinalizeField('student_course_id', v)}
                            courses={courses}
                            placeholder={courses.length === 0 ? 'No courses' : 'Select course'}
                            testid="fin-course"
                          />
                        </Field>
                        <Field label="Year">
                          <Select value={finalizeForm.curr_year_of_study} onValueChange={v => updateFinalizeField('curr_year_of_study', v)}>
                            <SelectTrigger className="rounded-xl border-[#E2E8E5] h-11" data-testid="fin-year"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {YEARS_OF_STUDY.map(y => <SelectItem key={y} value={y}>Year {y}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </Field>
                      </div>
                    </>
                  ) : (
                    <Field label="ID Number *">
                      <Input value={finalizeForm.id_number} onChange={e => updateFinalizeField('id_number', e.target.value)}
                        placeholder="National ID / Passport / etc." className="rounded-xl border-[#E2E8E5] h-11" data-testid="fin-id-number" />
                    </Field>
                  )}

                  <Button onClick={handleFinalizeSubscriber} disabled={finalizeSubmitting}
                    className="w-full h-12 bg-[#0779BC] hover:bg-[#0779BC]/90 text-white rounded-xl text-base font-medium disabled:opacity-60" data-testid="fin-submit">
                    {finalizeSubmitting ? 'Finalizing…' : 'Finalize Registration'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ========== RECHARGE CARD ========== */}
        {view === 'recharge' && (
          <div className="mt-4" data-testid="cashier-recharge">
            <div className="border border-[#E2E8E5] rounded-2xl overflow-hidden bg-white shadow-sm">
              {/* Form header */}
              <div className="px-5 py-4 border-b border-[#E2E8E5] bg-gradient-to-r from-[#0779BC]/5 via-white to-[#0779BC]/5 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#0779BC] text-white flex items-center justify-center">
                  <CurrencyDollar size={20} weight="bold" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-[#1A1D1C]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                    Recharge Card
                  </h2>
                  <p className="text-[11px] text-[#6C7A75]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                    Fields marked * are required
                  </p>
                </div>
              </div>

              {/* Form body */}
              <div className="px-5 py-5 space-y-4">
                <Field label="Card Number *">
                  <div className="relative">
                    <Input
                      value={rechargeCard}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g, '');
                        setRechargeCard(v);
                        if (v.length >= 10) verifyRechargeCard(v);
                        else { setRechargeCardStatus(null); setRechargeCardHolder(''); }
                      }}
                      placeholder="Enter card number"
                      inputMode="numeric"
                      className="rounded-xl border-[#E2E8E5] h-11 pr-10"
                      data-testid="rech-card"
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {rechargeCardStatus === 'checking' && <Spinner size={18} className="animate-spin text-[#6C7A75]" />}
                      {rechargeCardStatus === 'valid' && <CheckCircle size={18} weight="fill" className="text-[#2E7D5B]" />}
                      {(rechargeCardStatus === 'invalid' || rechargeCardStatus === 'unassigned') && <XCircle size={18} weight="fill" className="text-[#B94040]" />}
                    </div>
                  </div>
                  {rechargeCardStatus === 'invalid' && <p className="text-xs text-[#B94040] mt-1">Card not found in your entity</p>}
                  {rechargeCardStatus === 'unassigned' && <p className="text-xs text-[#B94040] mt-1">Card has no subscriber attached</p>}
                  {rechargeCardStatus === 'valid' && rechargeCardHolder && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-[#E6F4EE] border border-[#2E7D5B]/30 flex items-center gap-2" data-testid="rech-card-holder">
                      <CheckCircle size={14} weight="fill" className="text-[#2E7D5B]" />
                      <p className="text-xs text-[#1A1D1C]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                        Holder: <span className="font-semibold">{rechargeCardHolder}</span>
                      </p>
                    </div>
                  )}
                  {rechargeCardStatus === 'valid' && !rechargeCardHolder && (
                    <p className="text-xs text-[#2E7D5B] mt-1">Card valid · subscriber name unavailable</p>
                  )}
                </Field>

                <Field label="Amount *">
                  <div className="relative">
                    <Input
                      value={rechargeAmount ? Number(rechargeAmount).toLocaleString('en-US') : ''}
                      onChange={e => {
                        const digits = e.target.value.replace(/\D/g, '');
                        setRechargeAmount(digits);
                      }}
                      placeholder="0"
                      inputMode="numeric"
                      className="rounded-xl border-[#E2E8E5] h-11 pr-16 text-right font-semibold tracking-wide"
                      style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                      data-testid="rech-amount"
                    />
                    {(stats?.currency || '') && (
                      <span
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-[#6C7A75] pointer-events-none"
                        style={{ fontFamily: 'IBM Plex Mono, monospace' }}
                      >
                        {stats.currency}
                      </span>
                    )}
                  </div>
                </Field>

                <Field label="Category *">
                  {(() => {
                    const offerEnabled = (stats?.special_offer_enabled === 'Y') || ['Y', 'Yes'].includes(cashierConfig?.offer);
                    if (!offerEnabled) {
                      return (
                        <Input value="Recharge" disabled
                          className="rounded-xl border-[#E2E8E5] h-11 bg-[#F4F5F4] text-[#6C7A75]"
                          data-testid="rech-category-locked" />
                      );
                    }
                    return (
                      <Select value={rechargeCategory} onValueChange={setRechargeCategory}>
                        <SelectTrigger className="rounded-xl border-[#E2E8E5] h-11" data-testid="rech-category">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Recharge">Recharge</SelectItem>
                          <SelectItem value="Special Offer">Special Offer</SelectItem>
                        </SelectContent>
                      </Select>
                    );
                  })()}
                  {rechargeCategory === 'Special Offer' && (
                    <p className="text-[11px] text-[#2E7D5B] mt-1.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      Special Offer — your float will not be deducted.
                    </p>
                  )}
                  {(stats?.recharge_limit ?? 0) > 0 && (
                    <p className="text-[11px] text-[#6C7A75] mt-1" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      Per-recharge limit: {Number(stats.recharge_limit).toLocaleString()} {stats?.currency || ''}
                    </p>
                  )}
                </Field>
              </div>

              {/* Form footer / submit */}
              <div className="px-5 py-4 bg-[#F8F9F9] border-t border-[#E2E8E5]">
                <Button onClick={async () => {
                  if (!rechargeCard || !rechargeAmount) { toast.error('Card and amount are required'); return; }
                  if (rechargeCardStatus !== 'valid') {
                    toast.error(
                      rechargeCardStatus === 'unassigned'
                        ? 'Card has no subscriber attached'
                        : 'Card number is invalid for your entity'
                    );
                    return;
                  }
                  setRechargeSubmitting(true);
                  try {
                    const offerEnabled = (stats?.special_offer_enabled === 'Y') || ['Y', 'Yes'].includes(cashierConfig?.offer);
                    const category = offerEnabled ? rechargeCategory : 'Recharge';
                    const payload = {
                      card_no: rechargeCard,
                      amount: parseFloat(rechargeAmount),
                      special_offer: category === 'Special Offer' ? 'Y' : 'N',
                    };
                    const { data } = await axios.post(`${API_URL}/api/recharge-card`, payload, { withCredentials: true, headers: hdrs() });
                    const cur = data?.currency || stats?.currency || '';
                    const closeBal = data?.wallet_close_bal != null ? Number(data.wallet_close_bal).toLocaleString() : null;
                    const subjectName = data?.sub_name ? ` for ${data.sub_name}` : (rechargeCardHolder ? ` for ${rechargeCardHolder}` : '');
                    const headline = category === 'Special Offer' ? 'Special offer recharge applied' : 'Card recharged successfully';
                    toast.success(
                      closeBal
                        ? `${headline}${subjectName} — new balance: ${closeBal} ${cur}${data?.trn_ref_no ? ` (Ref ${data.trn_ref_no})` : ''}`
                        : headline
                    );
                    setRechargeCard('');
                    setRechargeAmount('');
                    setRechargeCategory('Recharge');
                    setRechargeCardStatus(null);
                    setRechargeCardHolder('');
                    fetchStats();
                    setView('home');
                  } catch (e) {
                    toast.error(formatApiError(e, 'Failed to recharge card'));
                  } finally { setRechargeSubmitting(false); }
                }} disabled={rechargeSubmitting || rechargeCardStatus !== 'valid'}
                  className="w-full h-12 bg-[#0779BC] hover:bg-[#0779BC]/90 text-white rounded-xl text-base font-medium disabled:opacity-60" data-testid="rech-submit">
                  {rechargeSubmitting ? 'Recharging…' : `Recharge${rechargeAmount ? ` ${Number(rechargeAmount).toLocaleString('en-US')}${stats?.currency ? ' ' + stats.currency : ''}` : ''}`}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ========== LOGS ========== */}
        {view === 'logs' && (
          <div className="mt-4 space-y-4" data-testid="cashier-logs">
            <div className="flex gap-2">
              <button onClick={() => { setExpandedLog(null); fetchLogs('registrations'); }}
                className={`flex-1 py-2 rounded-xl text-sm font-medium ${logType === 'registrations' ? 'bg-[#0779BC] text-white' : 'bg-white border border-[#E2E8E5] text-[#6C7A75]'}`}
                data-testid="log-tab-reg">Registrations</button>
              <button onClick={() => { setExpandedLog(null); fetchLogs('recharges'); }}
                className={`flex-1 py-2 rounded-xl text-sm font-medium ${logType === 'recharges' ? 'bg-[#0779BC] text-white' : 'bg-white border border-[#E2E8E5] text-[#6C7A75]'}`}
                data-testid="log-tab-rech">Recharges</button>
            </div>

            {logsLoading ? (
              <div className="text-center py-8"><Spinner size={24} className="animate-spin text-[#6C7A75] mx-auto" /></div>
            ) : logs.length === 0 ? (
              <div className="text-center py-8 text-[#6C7A75] text-sm">No {logType === 'registrations' ? 'registrations' : 'recharges'} today</div>
            ) : (
              <div className="space-y-2">
                {logs.map((l, i) => {
                  const isOpen = expandedLog === (l.id ?? i);
                  if (logType === 'registrations') {
                    return (
                      <div key={l.id || i} className="bg-white rounded-xl border border-[#E2E8E5]" data-testid={`log-row-${i}`}>
                        <div className="flex items-center justify-between p-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-sm text-[#1A1D1C] truncate" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                              {l.sub_name || '—'}
                            </p>
                            <p className="text-[11px] text-[#6C7A75] truncate" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                              {l.mobile || '—'} {l.category ? `· ${l.category}` : ''}
                            </p>
                            <p className="text-[10px] text-[#A8A29E] uppercase tracking-wider mt-0.5" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                              {l.plan || '—'} · {l.campus_name || '—'}
                            </p>
                          </div>
                          <button
                            onClick={() => setExpandedLog(isOpen ? null : (l.id ?? i))}
                            data-testid={`log-view-${i}`}
                            className="ml-3 shrink-0 w-9 h-9 rounded-full bg-[#0779BC]/10 flex items-center justify-center active:bg-[#0779BC]/20"
                          >
                            <Eye size={18} weight={isOpen ? 'fill' : 'regular'} className="text-[#0779BC]" />
                          </button>
                        </div>
                        {isOpen && (
                          <div className="px-3 pb-3 pt-1 border-t border-[#E2E8E5]/60 grid grid-cols-2 gap-2 text-xs" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                            <div>
                              <p className="text-[10px] text-[#6C7A75] uppercase tracking-wider">Card No.</p>
                              <p className="text-[#1A1D1C] break-all">{l.card_no || '—'}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-[#6C7A75] uppercase tracking-wider">Plan</p>
                              <p className="text-[#1A1D1C]">{l.plan || '—'}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-[#6C7A75] uppercase tracking-wider">Phone</p>
                              <p className="text-[#1A1D1C]">{l.mobile || '—'}</p>
                            </div>
                            <div>
                              <p className="text-[10px] text-[#6C7A75] uppercase tracking-wider">Category</p>
                              <p className="text-[#1A1D1C]">{l.category || '—'}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-[10px] text-[#6C7A75] uppercase tracking-wider">Sub Fee</p>
                              <p className="text-[#0779BC] font-semibold">
                                {Number(l.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {l.currency || ''}
                              </p>
                              {l.plan && l.plan.toLowerCase() !== 'fee' && Number(l.raw_amount || 0) > 0 ? (
                                <p className="text-[10px] text-[#6C7A75] mt-1">
                                  Initial recharge: {Number(l.raw_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {l.currency || ''}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  // Recharges row
                  return (
                    <div key={l.id || i} className="bg-white rounded-xl border border-[#E2E8E5] p-3 flex items-center justify-between" data-testid={`log-row-${i}`}>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-[#1A1D1C] truncate" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
                          {l.sub_name || '—'}
                        </p>
                        <p className="text-[11px] text-[#6C7A75] truncate" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                          {l.card_no || '—'}
                        </p>
                        {l.mobile ? (
                          <p className="text-[10px] text-[#A8A29E] truncate" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                            {l.mobile}
                          </p>
                        ) : null}
                      </div>
                      <div className="text-right ml-3 shrink-0">
                        <p className="text-sm font-semibold text-[#0779BC] font-mono">
                          {Number(l.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {l.currency || ''}
                        </p>
                        <p className="text-[10px] text-[#6C7A75]">{l.time?.split(' ')[1]?.substring(0, 5) || l.time?.split('T')[1]?.substring(0, 5) || ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, testId, muted = false, unit }) {
  return (
    <div className="bg-[#F8F9FA] rounded-xl px-4 py-3" data-testid={testId}>
      <p className="text-[10px] text-[#0779BC] uppercase tracking-[0.15em] font-semibold leading-tight" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{label}</p>
      <p
        className={`text-[18px] leading-tight font-bold font-mono mt-1 text-right ${muted ? 'text-[#1A1D1C]' : 'text-[#1A1D1C]'}`}
      >
        {value}
        {unit ? <span className="text-[10px] font-semibold text-[#6C7A75] ml-1.5 align-middle">{unit}</span> : null}
      </p>
    </div>
  );
}

function IconTile({ icon: Icon, label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="group flex flex-col items-center text-center gap-2 active:scale-[0.97] transition-transform"
    >
      <div className="w-14 h-14 rounded-full bg-[#0779BC]/10 flex items-center justify-center group-active:bg-[#0779BC]/20 transition-colors">
        <Icon size={22} weight="regular" className="text-[#0779BC]" />
      </div>
      <span className="text-[13px] leading-tight text-[#1A1D1C] font-medium px-1" style={{ fontFamily: 'Ubuntu, sans-serif' }}>
        {label}
      </span>
    </button>
  );
}

function ActionButton({ icon: Icon, label, onClick, testId, color }) {
  return (
    <button onClick={onClick} data-testid={testId}
      className="w-full flex items-center gap-4 bg-white rounded-xl border border-[#E2E8E5] p-4 active:bg-[#F4F5F4] transition-all">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: color }}>
        <Icon size={22} weight="bold" />
      </div>
      <span className="text-base font-medium text-[#1A1D1C]" style={{ fontFamily: 'Ubuntu, sans-serif' }}>{label}</span>
    </button>
  );
}

function Field({ label, children, error }) {
  return (
    <div className="space-y-1.5">
      <Label className={`text-xs ${error ? 'text-[#B94040] font-semibold' : 'text-[#6C7A75]'}`} style={{ fontFamily: 'Ubuntu, sans-serif' }}>{label}</Label>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-[#6C7A75]">{label}</span>
      <span className="text-[#1A1D1C] font-medium">{value || '-'}</span>
    </div>
  );
}
